import {
  Box,
  BoxExportedProps,
  H3,
  L40,
  L88,
  Stack,
  defaultTheme
} from "@adfenix/alexandria";
import { ResponsiveSpace } from "@adfenix/alexandria/dist/components/Layout/utils";
import classNames from "classnames";
import { ReactElement, ReactNode, useState } from "react";
import { Collapse } from "react-collapse";
import styled from "styled-components";

interface CollapsibleCardProps {
  title?: string | ReactElement;
  header?: ReactElement;
  leftHeader?: ReactElement;
  expanded?: boolean;
  children: ReactNode;
  p?: ResponsiveSpace[] | ResponsiveSpace;
  pX?: ResponsiveSpace[] | ResponsiveSpace;
  pY?: ResponsiveSpace[] | ResponsiveSpace;
  noBoxShadow?: boolean;
  stack?: boolean;

  /**
   * If the primary color should be used for the expand icon.
   */
  expandIconColor?: string;

  /**
   * Styles for the parent component.
   */
  style?: React.CSSProperties;

  /**
   * If the card should be expanded initially. Only useful when omitting expanded.
   */
  initiallyExpanded?: boolean;
  expandIcon?: ReactNode;
  collapseIcon?: ReactNode;
}

const StyledStack = styled(
  ({
    p,
    pY,
    pX,
    stack,
    ...rest
  }: BoxExportedProps & { stack: boolean; noBoxShadow?: boolean }) =>
    stack ? (
      <Stack spacing="xs" pY={pY} pX={pX} p={p ?? ["s", "xl2"]} {...rest} />
    ) : (
      <Box pY={pY} pX={pX} p={p ?? ["s", "xl2"]} {...rest} />
    )
)`
  color: ${({ theme }) => L40(theme.colors.grey)};
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.borderRadius.m}

  box-shadow: ${({ theme, noBoxShadow }) =>
    noBoxShadow
      ? "none"
      : `0 1px 4px 0 ${L88(theme.colors.grey)}, 0 0 0 1px ${L88(
          theme.colors.grey
        )}`};
`;

const HoverableBox = styled(Box)<{ toggled: boolean }>`
  cursor: pointer;
`;

const ExpandIcon = styled.i<{ expandIconColor: string }>`
  color: ${({ expandIconColor }) => expandIconColor};
`;

function CollapsibleCard({
  header,
  leftHeader,
  title,
  p,
  pY,
  pX,
  noBoxShadow,
  children,
  expanded = false,
  initiallyExpanded = false,
  expandIconColor = defaultTheme.colors.primary,
  collapseIcon: collapsIcon,
  expandIcon,
  stack = true,
  ...rest
}: CollapsibleCardProps) {
  const [toggled, setToggled] = useState(expanded || initiallyExpanded);

  const toggleContent = () => {
    setToggled(!toggled);
  };

  return (
    <StyledStack
      p={p}
      pY={pY}
      pX={pX}
      noBoxShadow={noBoxShadow}
      {...rest}
      stack={stack}
    >
      <HoverableBox
        toggled={expanded || toggled}
        onClick={toggleContent}
        display="flex"
        justifyContent={"space-between"}
        alignItems="center"
      >
        {leftHeader}
        {typeof title === "string" ? <H3>{title}</H3> : title}

        <Box display="flex" justifyContent="space-between" alignItems="center">
          {header}

          {!expandIcon && !expanded && (
            <ExpandIcon
              expandIconColor={expandIconColor}
              className={classNames(
                toggled || expanded
                  ? "uil uil-angle-up is-size-4"
                  : "uil uil-angle-down is-size-4",
                "no-print"
              )}
            />
          )}

          {expandIcon && (expanded || toggled ? collapsIcon : expandIcon)}
        </Box>
      </HoverableBox>

      <Collapse isOpened={expanded || toggled}>{children}</Collapse>
    </StyledStack>
  );
}

export default CollapsibleCard;
